import React from 'react';

import { countReactions, reactorName } from '../../config/util/reactions';

/**
 * The reaction pills that hang under a message bubble.
 *
 * Every pane rendered its own copy of this markup — five in total — and they had
 * drifted apart: the group pane's own-message copy sat OUTSIDE the bubble row, so
 * the pills detached and floated a line below the bubble, and the direct-message
 * own-bubble copy used absolutely-positioned Tailwind with a `+N` suffix while
 * every other copy used `.reaction-pill` with a bare count. One component now
 * serves all of them, so a group reaction looks exactly like a 1:1 reaction.
 *
 * Multiple people can react, and to different emojis: one pill per emoji, with
 * the number of people who picked it and a hover title naming them. The viewer's
 * own pick is highlighted, and clicking any pill opens the full reactor list.
 *
 * Renders nothing when there are no reactions — the old unconditional wrapper
 * left an empty flex row under every message, which is what pushed the pills off
 * the bubble in the group pane.
 *
 * @param reactions raw `chat_reactions` for the message
 * @param currentUserId viewer, used to highlight their own pick
 * @param isOut true for the viewer's own messages (pills align right)
 * @param floating pin the pills to the bubble's bottom edge instead of letting
 *   them take their own row. Own-message bubbles need this: they sit in a
 *   `justify-end` row and cap themselves at `max-width: 70%` OF THE ROW, so
 *   wrapping them in a column to stack the pills underneath re-resolves that
 *   percentage against a shrink-to-fit parent — text bubbles collapse to a few
 *   characters and fixed-width media overflows the pane. Staying out of flow
 *   leaves the bubble the row's direct child and its geometry untouched.
 * @param onOpenList click handler, receives the raw reactions
 */
const MessageReactions = ({
  reactions,
  currentUserId,
  isOut = false,
  floating = false,
  onOpenList,
}) => {
  const tallied = countReactions(reactions, currentUserId);
  if (tallied.length === 0) return null;

  return (
    <div
      className={`msg-reactions ${isOut ? 'is-out' : ''} ${floating ? 'is-floating' : ''}`}
      onClick={onOpenList ? () => onOpenList(reactions) : undefined}
    >
      {tallied.map((entry) => {
        const names = (entry.reactors || []).map(reactorName).filter(Boolean);
        return (
          <span
            key={entry.emoji_id}
            className={`reaction-pill ${entry.mine ? 'is-mine' : ''}`}
            title={names.length ? names.join(', ') : undefined}
          >
            <span className="reaction-pill-emoji">{entry.emoji_id}</span>
            {entry.Count > 1 ? <b>{entry.Count}</b> : null}
          </span>
        );
      })}
    </div>
  );
};

export default MessageReactions;
